/**
 * Main Application Entry Point
 * Initializes and coordinates physics, visualization, and controls
 */

import { GravityAssistPhysics, PhysicsState, PhysicsInputs } from './simulation/physics';
import { Visualizer } from './rendering/visualizer';
import { Controls } from './ui/controls';
import './styles.css';

interface AnimationParameters extends PhysicsInputs {
  animate: boolean;
  speed: number;
}

class GravityAssistApp {
  private physics: GravityAssistPhysics;
  private visualizer: Visualizer;
  private controls: Controls;
  private currentState: PhysicsState | null = null;

  // Animation state
  private isAnimating = false;
  private animationTime = 0;
  private lastFrameTime = 0;
  private animationFrameId: number | null = null;
  private animationSpeed = 1;
  private currentV0 = 0;

  constructor() {
    // Initialize physics engine
    this.physics = new GravityAssistPhysics();

    // Get canvas elements
    const canvasPlanet = document.getElementById('canvas-planet') as HTMLCanvasElement;
    const canvasSun = document.getElementById('canvas-sun') as HTMLCanvasElement;

    if (!canvasPlanet || !canvasSun) {
      throw new Error('Canvas elements not found');
    }

    // Initialize visualizer
    this.visualizer = new Visualizer(canvasPlanet, canvasSun);

    // Initialize controls
    this.controls = new Controls((params) => this.onParametersChanged(params));
  }

  /**
   * Called when parameters change
   */
  private onParametersChanged(params: AnimationParameters): void {
    try {
      // Toggle single-panel mode based on v0
      const vizContainer = document.querySelector('.viz-container');
      if (vizContainer) {
        if (params.v0 === 0) {
          vizContainer.classList.add('single-panel');
        } else {
          vizContainer.classList.remove('single-panel');
        }
      }

      // Calculate max distance for trajectory based on zoom and canvas size
      const canvas = document.getElementById('canvas-planet') as HTMLCanvasElement;
      const canvasSize = Math.min(canvas.width, canvas.height);
      const baseViewDistance = 1e6; // km (base view at zoom=1)
      const baseScale = canvasSize * 0.4 / baseViewDistance;
      const scale = baseScale * (params.zoom || 1);

      // Trajectory should extend to ~90% of viewport radius
      const viewportRadius = canvasSize / 2;
      const maxDist = viewportRadius * 0.9 / scale;

      // Compute physics
      const state = this.physics.compute({
        ...params,
        maxDist
      });

      // Store state
      this.currentState = state;

      // Handle animation mode changes
      if (params.animate && !this.isAnimating) {
        this.startAnimation();
      } else if (!params.animate && this.isAnimating) {
        this.stopAnimation();
      } else if (params.animate && this.isAnimating) {
        // If already animating and parameters changed, reset to beginning
        const minT = Math.min(...state.trajectory.map(p => p.t));
        this.animationTime = minT;
      }

      // Store animation parameters
      this.animationSpeed = params.speed || 1;
      this.currentV0 = params.v0;

      // Update visualizations (static or current animation frame)
      if (!params.animate) {
        this.visualizer.renderPlanetFrame(state, null);
        if (params.v0 > 0) {
          this.visualizer.renderSunFrame(state, null);
        }
      }

      // Update readouts
      this.controls.updateReadouts(state);

      // Display warnings/status
      this.controls.displayStatus(state.warnings);

      // Validation (optional, for debugging)
      const validation = this.physics.validateTrajectory(state.trajectory, state.U);
      if (!validation.isValid) {
        console.warn('Trajectory validation failed:', validation);
      } else {
        console.log('✓ Trajectory valid. Max speed deviation:', validation.maxSpeedDeviation.toExponential(2));
      }

    } catch (error) {
      console.error('Computation error:', error);

      // Display error
      this.controls.displayStatus([{
        type: 'error',
        msg: error instanceof Error ? error.message : 'Unknown error'
      }]);

      // Clear visualizations
      this.visualizer.renderPlanetFrame(null, null);
      this.visualizer.renderSunFrame(null, null);

      // Clear readouts
      this.controls.updateReadouts(null);
    }
  }

  /**
   * Start animation loop
   */
  private startAnimation(): void {
    if (this.isAnimating) return;

    this.isAnimating = true;
    if (this.currentState && this.currentState.trajectory) {
      const trajectory = this.currentState.trajectory;
      const minT = Math.min(...trajectory.map(p => p.t));
      this.animationTime = minT;

      // Debug: log trajectory info
      const firstPoint = trajectory[0];
      const lastPoint = trajectory[trajectory.length - 1];
      console.log(`Animation starting: b=${this.currentState.b}, minT=${minT.toFixed(2)}`);
      console.log(`  First point: t=${firstPoint.t.toFixed(2)}, vel=(${firstPoint.u_x.toFixed(2)}, ${firstPoint.u_y.toFixed(2)})`);
      console.log(`  Last point: t=${lastPoint.t.toFixed(2)}, vel=(${lastPoint.u_x.toFixed(2)}, ${lastPoint.u_y.toFixed(2)})`);
      console.log(`  u_in=(${this.currentState.u_in.x.toFixed(2)}, ${this.currentState.u_in.y.toFixed(2)})`);
      console.log(`  u_out=(${this.currentState.u_out.x.toFixed(2)}, ${this.currentState.u_out.y.toFixed(2)})`);
    } else {
      this.animationTime = 0;
    }
    this.lastFrameTime = performance.now();
    this.animationLoop();
  }

  /**
   * Stop animation loop
   */
  private stopAnimation(): void {
    this.isAnimating = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Animation loop
   */
  private animationLoop(): void {
    if (!this.isAnimating || !this.currentState) return;

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastFrameTime) / 1000; // Convert to seconds
    this.lastFrameTime = currentTime;

    // Update animation time
    const trajectory = this.currentState.trajectory;
    const minT = Math.min(...trajectory.map(p => p.t));
    const maxT = Math.max(...trajectory.map(p => p.t));
    const duration = maxT - minT;

    // Advance time based on speed
    const timeStep = deltaTime * this.animationSpeed * duration / 10;
    this.animationTime += timeStep;

    // Loop animation
    if (this.animationTime > maxT) {
      this.animationTime = minT;
    }

    // Render current frame
    this.visualizer.renderPlanetFrame(this.currentState, this.animationTime);
    if (this.currentV0 > 0) {
      this.visualizer.renderSunFrame(this.currentState, this.animationTime);
    }

    // Continue animation
    this.animationFrameId = requestAnimationFrame(() => this.animationLoop());
  }

  /**
   * Initialize the app
   */
  initialize(): void {
    console.log('Gravity Assist Sandbox initializing...');

    // Load default preset
    this.controls.initialize();

    console.log('✓ App ready');
  }

  /**
   * Run sanity checks for validation
   */
  runSanityChecks(): { passed: number; failed: number; total: number } {
    console.log('Running sanity checks...');

    interface Test {
      name: string;
      params: PhysicsInputs | PhysicsInputs[];
      check: (state: PhysicsState | PhysicsState[]) => { pass: boolean; msg: string };
    }

    const tests: Test[] = [
      {
        name: 'Zero turn (large b)',
        params: { mu: 398600, v0: 30, v: 15, theta: 180, b: 1e6, Rpl: 6371 },
        check: (state) => {
          const s = state as PhysicsState;
          const deltaSmall = s.delta < 0.01;
          const vOutClose = Math.abs(s.v_out_mag - s.v_in_mag) < 0.1;
          return { pass: deltaSmall && vOutClose, msg: `δ = ${(s.delta * 180 / Math.PI).toFixed(4)}°` };
        }
      },
      {
        name: 'Symmetry test (±b)',
        params: [
          { mu: 398600, v0: 30, v: 15, theta: 180, b: 50000, Rpl: 6371 },
          { mu: 398600, v0: 30, v: 15, theta: 180, b: -50000, Rpl: 6371 }
        ],
        check: (states) => {
          const [s1, s2] = states as PhysicsState[];
          const deltaSame = Math.abs(s1.delta - s2.delta) < 1e-10;
          const rpSame = Math.abs(s1.rp - s2.rp) < 1e-6;
          return {
            pass: deltaSame && rpSame,
            msg: `δ₁ = ${s1.delta.toFixed(6)}, δ₂ = ${s2.delta.toFixed(6)}`
          };
        }
      },
      {
        name: 'Energy gain bound',
        params: { mu: 398600, v0: 30, v: 15, theta: 180, b: 50000, Rpl: 6371 },
        check: (state) => {
          const s = state as PhysicsState;
          const maxSpeed = s.v0 + s.U;
          const minSpeed = Math.abs(s.v0 - s.U);
          const inBounds = s.v_out_mag >= minSpeed - 0.1 && s.v_out_mag <= maxSpeed + 0.1;
          return {
            pass: inBounds,
            msg: `${minSpeed.toFixed(2)} ≤ ${s.v_out_mag.toFixed(2)} ≤ ${maxSpeed.toFixed(2)}`
          };
        }
      },
      {
        name: 'Speed invariance in P-frame',
        params: { mu: 398600, v0: 30, v: 15, theta: 180, b: 50000, Rpl: 6371 },
        check: (state) => {
          const s = state as PhysicsState;
          const validation = this.physics.validateTrajectory(s.trajectory, s.U);
          return {
            pass: validation.isValid,
            msg: `Max relative deviation: ${(validation.maxRelativeDeviation * 1e6).toFixed(2)} ppm`
          };
        }
      },
      {
        name: 'Periapsis check',
        params: { mu: 398600, v0: 30, v: 15, theta: 180, b: 50000, Rpl: 6371 },
        check: (state) => {
          const s = state as PhysicsState;
          const minR = Math.min(...s.trajectory.map(p => p.r));
          const rpMatch = Math.abs(minR - s.rp) < 1e-6;
          return {
            pass: rpMatch,
            msg: `r_p (computed) = ${s.rp.toFixed(2)}, r_p (min) = ${minR.toFixed(2)}`
          };
        }
      }
    ];

    let passed = 0;
    let failed = 0;

    tests.forEach((test, i) => {
      try {
        if (Array.isArray(test.params)) {
          // Multi-state test
          const states = test.params.map(p => this.physics.compute(p));
          const result = test.check(states);

          console.log(`Test ${i + 1}: ${test.name}`);
          console.log(`  ${result.pass ? '✓ PASS' : '✗ FAIL'} - ${result.msg}`);

          if (result.pass) passed++;
          else failed++;
        } else {
          // Single-state test
          const state = this.physics.compute(test.params);
          const result = test.check(state);

          console.log(`Test ${i + 1}: ${test.name}`);
          console.log(`  ${result.pass ? '✓ PASS' : '✗ FAIL'} - ${result.msg}`);

          if (result.pass) passed++;
          else failed++;
        }
      } catch (error) {
        console.log(`Test ${i + 1}: ${test.name}`);
        console.log(`  ✗ ERROR - ${error instanceof Error ? error.message : 'Unknown error'}`);
        failed++;
      }
    });

    console.log(`\nResults: ${passed} passed, ${failed} failed out of ${tests.length} tests`);
    return { passed, failed, total: tests.length };
  }
}

// Initialize app when DOM is ready
let app: GravityAssistApp;

document.addEventListener('DOMContentLoaded', () => {
  app = new GravityAssistApp();
  app.initialize();

  // Expose app globally for debugging
  (window as any).gravityAssistApp = app;

  console.log('Tip: Run gravityAssistApp.runSanityChecks() in console to validate physics');
});
